/**
 * Administração › Marca: nome, contatos, logomarca e cores do integrador — Nexora Grid (frontend, US-4503, Sprint 45).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Uma instalação por integrador (D-4501). O administrador da plataforma (D-4502) edita o RASCUNHO, confere a prévia
 * nos três modos de exibição (escuro, claro, alto contraste) e PUBLICA com motivo; cada publicação é uma versão nova
 * no histórico e qualquer uma pode ser restaurada. O suporte só consulta. Cores de alarme não são da marca (D-4503):
 * a validação ajusta a cor de cada modo até ter contraste e avisa quando ela lembra uma cor de alarme.
 * API: `backend/marca_api.py`.
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { api, ErroApi } from "../api";
import { useMarca, type Marca as MarcaPublica, type ModoExibicao, MODOS } from "../marca";
import { AbasAdmin } from "./AbasAdmin";
import { useSessao } from "../sessao";
import {
  Abas, avisar, CabecalhoTela, Campo, DialogoMotivo, ErroCarregar, Esqueleto, GrupoCampos, RodapeFormulario, Selo, useAvisoAoSair, useParametro,
} from "../componentes/Tela";
import { idadeRelativa } from "../componentes/regrasTela";
import "./marca.css";

type Validacao = { bloqueios: string[]; avisos: string[]; cores_por_modo: MarcaPublica["cores_por_modo"] };
type Versao = { versao: number; origem: string; restaurada_de: number | null; motivo: string; publicado_por: string; publicado_em: string; produto: string };
type Resumo = {
  pode_editar: boolean; publicada: MarcaPublica;
  rascunho: MarcaPublica & { tem_alteracoes: boolean; alterado_por: string | null; alterado_em: string | null };
  validacao: Validacao; historico: Versao[];
};
type Textos = Pick<MarcaPublica, "produto" | "nome_curto" | "empresa" | "razao_social" | "cnpj" | "site" | "email_suporte" | "telefone_suporte" | "remetente_email">
  & { cores: MarcaPublica["cores"] };
type Aba = "identidade" | "logomarca" | "cores" | "historico" | "pacote";

const ABAS: { chave: Aba; texto: string }[] = [
  { chave: "identidade", texto: "Identidade" }, { chave: "logomarca", texto: "Logomarca" }, { chave: "cores", texto: "Cores" },
  { chave: "historico", texto: "Histórico" }, { chave: "pacote", texto: "Pacote" }];
const ORIGEM: Record<string, string> = { publicacao: "Publicada", restauracao: "Restaurada", padrao: "Voltou ao padrão", importacao: "Importada" };
const CAMPOS: { chave: keyof Textos; rotulo: string; ajuda?: string; largo?: boolean }[] = [
  { chave: "produto", rotulo: "Nome do produto", ajuda: "Aparece no título da aba, nos e-mails e nos canais" },
  { chave: "nome_curto", rotulo: "Nome curto", ajuda: "Ao lado da logomarca; o resto do nome vira o sufixo (ex.: GRID)" },
  { chave: "empresa", rotulo: "Empresa integradora", ajuda: "\"equipe <empresa>\" nas mensagens" },
  { chave: "razao_social", rotulo: "Razão social" }, { chave: "cnpj", rotulo: "CNPJ" },
  { chave: "site", rotulo: "Site", ajuda: "Comece com https://" }, { chave: "email_suporte", rotulo: "E-mail de suporte" },
  { chave: "telefone_suporte", rotulo: "Telefone de suporte" }, { chave: "remetente_email", rotulo: "Remetente dos e-mails" },
];
const CORES: { chave: keyof MarcaPublica["cores"]; rotulo: string; uso: string }[] = [
  { chave: "primaria", rotulo: "Primária", uso: "nome da marca e cor do navegador" },
  { chave: "secundaria", rotulo: "Secundária", uso: "detalhes da marca" },
  { chave: "destaque", rotulo: "Destaque", uso: "seleção, foco e botão principal" }];

function textosDe(m: MarcaPublica): Textos {
  return { produto: m.produto, nome_curto: m.nome_curto, empresa: m.empresa, razao_social: m.razao_social, cnpj: m.cnpj, site: m.site,
    email_suporte: m.email_suporte, telefone_suporte: m.telefone_suporte, remetente_email: m.remetente_email, cores: { ...m.cores } };
}

function lerArquivo(arquivo: File): Promise<string> {
  return new Promise((ok, falha) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(String(leitor.result));
    leitor.onerror = () => falha(leitor.error);
    leitor.readAsDataURL(arquivo);
  });
}

/** Prévia da marca num modo: barra do sistema, login e um card do CCO, com as cores do rascunho já ajustadas. */
function Previa({ modo, textos, rascunho, cores }: { modo: ModoExibicao; textos: Textos; rascunho: MarcaPublica; cores: Validacao["cores_por_modo"] }) {
  const c = cores[modo] ?? rascunho.cores_por_modo[modo];
  const logo = modo === "claro" ? rascunho.arquivos.colorida.url : rascunho.arquivos.negativa.url;
  const sufixo = textos.produto.startsWith(textos.nome_curto) ? textos.produto.slice(textos.nome_curto.length).trim() : "";
  const estilo = { [`--marca-primaria-${modo}`]: c.primaria, [`--marca-secundaria-${modo}`]: c.secundaria, [`--marca-destaque-${modo}`]: c.destaque } as Record<string, string>;
  return (
    <figure className="previa-marca" data-tema="cco" data-modo={modo} id={undefined} style={estilo}>
      <figcaption>{MODOS.find((m) => m.valor === modo)?.texto}</figcaption>
      <div className="pm-barra"><img src={logo} alt={textos.nome_curto} />{sufixo && <span>{sufixo.toUpperCase()}</span>}
        <span className="pm-aba ativa">CCO</span><span className="pm-aba">Rede</span><span className="pm-aba">Cadastro</span></div>
      <div className="pm-corpo">
        <div className="pm-cartao"><small>PRESSÃO · PP-07</small><strong>9,4 <em>mca</em></strong><span className="pm-alarme">◆ Crítico</span></div>
        <div className="pm-cartao"><small>NÍVEL · RAP-01</small><strong>68 <em>%</em></strong><span className="nx-suave">Normal</span></div>
        <div className="pm-acoes"><button type="button" className="nx-botao nx-botao-principal" tabIndex={-1}>Salvar</button>
          <button type="button" className="nx-botao" tabIndex={-1}>Cancelar</button><span className="pm-foco">foco</span></div>
      </div>
      <div className="pm-rodape">{textos.produto} · {textos.email_suporte || "sem e-mail de suporte"}</div>
    </figure>
  );
}

export function Marca() {
  const { usuario } = useSessao();
  const { recarregar } = useMarca();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [textos, setTextos] = useState<Textos | null>(null);
  const [sujo, setSujo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useParametro("aba", "identidade");
  const [dialogo, setDialogo] = useState<null | { tipo: "publicar" | "padrao" | "restaurar"; versao?: number }>(null);
  useAvisoAoSair(sujo);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await api<Resumo>("/v1/admin/marca");
      setResumo(r);
      setTextos(textosDe(r.rascunho));
      setSujo(false);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : "Não foi possível carregar a marca.");
    }
  }, []);
  useEffect(() => { void carregar(); }, [carregar]);

  const aplicar = (r: Resumo, mensagem?: string) => {
    setResumo(r);
    setTextos(textosDe(r.rascunho));
    setSujo(false);
    if (mensagem) avisar(mensagem);
  };
  const chamar = async (fazer: () => Promise<Resumo>, mensagem: string) => {
    setSalvando(true);
    try { aplicar(await fazer(), mensagem); return true; } catch (e) {
      avisar(e instanceof ErroApi ? e.message : "Não foi possível concluir. Tente de novo.", "erro"); return false;
    } finally { setSalvando(false); }
  };
  const salvarRascunho = () => textos && chamar(() => api<Resumo>("/v1/admin/marca/rascunho", { metodo: "PUT", corpo: textos }), "Rascunho salvo");
  const enviarLogo = async (papel: "colorida" | "negativa" | "icone", ev: ChangeEvent<HTMLInputElement>) => {
    const arquivo = ev.target.files?.[0];
    ev.target.value = "";
    if (!arquivo) return;
    const tipo = arquivo.type === "image/svg+xml" || arquivo.name.toLowerCase().endsWith(".svg") ? "image/svg+xml" : "image/png";
    if (sujo && textos) await api("/v1/admin/marca/rascunho", { metodo: "PUT", corpo: textos }).catch(() => undefined);
    await chamar(async () => api<Resumo>("/v1/admin/marca/rascunho/arquivo", { corpo: { papel, tipo, base64: await lerArquivo(arquivo) } }),
      "Logomarca no rascunho");
  };
  const exportar = async () => {
    const sessao = JSON.parse(sessionStorage.getItem("nexora.sessao") ?? "{}") as { token?: string };
    const r = await fetch("/api/v1/admin/marca/exportar", { headers: sessao.token ? { Authorization: `Bearer ${sessao.token}` } : {} });
    if (!r.ok) { avisar("Não foi possível exportar.", "erro"); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = Object.assign(document.createElement("a"), { href: url,
      download: (r.headers.get("content-disposition") ?? "").match(/filename="([^"]+)"/)?.[1] ?? "marca.zip" });
    a.click();
    URL.revokeObjectURL(url);
  };
  const importar = async (ev: ChangeEvent<HTMLInputElement>) => {
    const arquivo = ev.target.files?.[0];
    ev.target.value = "";
    if (arquivo) await chamar(async () => api<Resumo>("/v1/admin/marca/importar", { corpo: { base64: await lerArquivo(arquivo) } }),
      "Pacote no rascunho: revise e publique");
  };
  const confirmar = async (motivo: string) => {
    if (!dialogo) return;
    if (dialogo.tipo === "publicar" && sujo && textos) await api("/v1/admin/marca/rascunho", { metodo: "PUT", corpo: textos });
    const caminho = dialogo.tipo === "publicar" ? "/v1/admin/marca/publicar" : dialogo.tipo === "padrao" ? "/v1/admin/marca/padrao"
      : `/v1/admin/marca/restaurar/${dialogo.versao}`;
    const ok = await chamar(() => api<Resumo>(caminho, { corpo: { motivo } }),
      dialogo.tipo === "publicar" ? "Marca publicada: todos passam a ver a nova" : dialogo.tipo === "padrao" ? "Marca padrão publicada" : `Versão ${dialogo.versao} restaurada`);
    if (ok) { setDialogo(null); void recarregar(); }
  };

  const bloqueiosLocais = useMemo(() => {
    if (!textos) return [];
    const b: string[] = [];
    if (!textos.produto.trim()) b.push("Dê um nome ao produto.");
    if (!textos.nome_curto.trim()) b.push("Dê um nome curto.");
    if (!textos.empresa.trim()) b.push("Informe a empresa integradora.");
    if (textos.site && !/^https?:\/\//.test(textos.site)) b.push("O site começa com https://");
    if (Object.values(textos.cores).some((c) => !/^#[0-9a-fA-F]{6}$/.test(c))) b.push("Use cores no formato #RRGGBB.");
    return b;
  }, [textos]);

  if (!usuario) return null;
  const pode = resumo?.pode_editar ?? false;
  const migalhas = [{ texto: "Administração", para: "/admin/empresas" }, { texto: "Marca" }];
  if (erro) return <section className="tl-pagina"><AbasAdmin usuario={usuario} /><CabecalhoTela migalhas={migalhas} titulo="Marca" /><ErroCarregar mensagem={erro} aoTentar={carregar} /></section>;
  if (!resumo || !textos) return <section className="tl-pagina"><AbasAdmin usuario={usuario} /><CabecalhoTela migalhas={migalhas} titulo="Marca" /><Esqueleto /></section>;
  const v = resumo.validacao;
  const mudar = (chave: keyof Textos, valor: string) => { setTextos({ ...textos, [chave]: valor }); setSujo(true); };
  const mudarCor = (chave: keyof Textos["cores"], valor: string) => { setTextos({ ...textos, cores: { ...textos.cores, [chave]: valor } }); setSujo(true); };

  return (
    <section className="tl-pagina tela-marca">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={migalhas} titulo="Marca do integrador" subtitulo={`${resumo.publicada.produto} · versão ${resumo.publicada.versao || "padrão"}`}
        selos={resumo.rascunho.tem_alteracoes ? <Selo tom="atencao">Rascunho com alterações</Selo> : <Selo tom="ok">Publicada</Selo>}
        acoes={pode && (
          <>
            {resumo.rascunho.tem_alteracoes && <button type="button" className="tl-fantasma" disabled={salvando}
              onClick={() => void chamar(() => api<Resumo>("/v1/admin/marca/rascunho/descartar", { corpo: {} }), "Rascunho descartado")}>Descartar rascunho</button>}
            <button type="button" className="nx-botao nx-botao-principal" disabled={salvando || bloqueiosLocais.length > 0 || v.bloqueios.length > 0}
              onClick={() => setDialogo({ tipo: "publicar" })}>Publicar…</button>
          </>
        )} />
      {!pode && <p className="nx-suave" style={{ margin: 0 }}>Só o administrador da plataforma altera a marca. Você pode consultar.</p>}
      {(v.bloqueios.length > 0 || bloqueiosLocais.length > 0) && (
        <div className="tl-erro" role="alert"><p><b>Antes de publicar:</b> {[...bloqueiosLocais, ...v.bloqueios].join(" · ")}</p></div>
      )}
      <div className="marca-grade">
        <section className="tl-bloco" aria-label="Edição da marca">
          <Abas abas={ABAS} ativa={aba as Aba} aoMudar={setAba} rotulo="Partes da marca" />
          {aba === "identidade" && (
            <>
              <div className="tl-painel-aba">
                <GrupoCampos titulo="Nome e contatos">
                  {CAMPOS.map((c) => (
                    <Campo key={c.chave} id={`m-${c.chave}`} rotulo={c.rotulo} ajuda={c.ajuda} largo={c.largo}>
                      <input id={`m-${c.chave}`} value={String(textos[c.chave] ?? "")} disabled={!pode} onChange={(e) => mudar(c.chave, e.target.value)} />
                    </Campo>
                  ))}
                </GrupoCampos>
              </div>
              {pode && <RodapeFormulario sujo={sujo} valido={bloqueiosLocais.length === 0} salvando={salvando} aoSalvar={() => void salvarRascunho()}
                aoCancelar={() => { setTextos(textosDe(resumo.rascunho)); setSujo(false); }} rotuloSalvar="Salvar rascunho" />}
            </>
          )}
          {aba === "logomarca" && (
            <div className="tl-painel-aba">
              <p className="nx-suave" style={{ margin: 0 }}>SVG ou PNG de até 1 MB. O SVG é limpo de scripts e links externos. O ícone (quadrado) gera os
                ícones da aba e do celular (32, 192 e 512 px).</p>
              <div className="logos-marca">
                {([["colorida", "Colorida", "modo claro"], ["negativa", "Negativa", "modos escuro e alto contraste"], ["icone", "Ícone", "aba do navegador e celular"]] as const).map(([papel, rotulo, uso]) => (
                  <div key={papel} className={`logo-marca ${papel === "colorida" ? "fundo-claro" : ""}`}>
                    <img src={resumo.rascunho.arquivos[papel].url} alt={`Logomarca ${rotulo.toLowerCase()}`} />
                    <b>{rotulo}</b><small className="nx-suave">{uso}</small>
                    {pode && <label className="nx-botao">Trocar…<input type="file" accept=".svg,.png,image/svg+xml,image/png" className="visualmente-oculto"
                      onChange={(e) => void enviarLogo(papel, e)} disabled={salvando} /></label>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {aba === "cores" && (
            <>
              <div className="tl-painel-aba">
                <GrupoCampos titulo="Cores da marca">
                  {CORES.map((c) => (
                    <Campo key={c.chave} id={`cor-${c.chave}`} rotulo={`${c.rotulo} — ${c.uso}`}>
                      <div className="campo-cor">
                        <input type="color" aria-label={`Escolher a cor ${c.rotulo.toLowerCase()}`} value={/^#[0-9a-fA-F]{6}$/.test(textos.cores[c.chave]) ? textos.cores[c.chave] : "#000000"}
                          disabled={!pode} onChange={(e) => mudarCor(c.chave, e.target.value.toUpperCase())} />
                        <input id={`cor-${c.chave}`} className="tl-mono" value={textos.cores[c.chave]} disabled={!pode} maxLength={7}
                          onChange={(e) => mudarCor(c.chave, e.target.value)} />
                      </div>
                    </Campo>
                  ))}
                </GrupoCampos>
                <p className="nx-suave" style={{ margin: 0 }}>As cores de alarme (◆ crítico, ▲ alto, ● médio, ■ baixo) não mudam com a marca. Em cada modo, a cor é
                  ajustada até ter contraste mínimo (4,5:1; 7:1 no alto contraste).</p>
                {v.avisos.length > 0 && <ul className="avisos-marca">{v.avisos.map((a) => <li key={a}>{a}</li>)}</ul>}
                {sujo && <p className="nx-suave" style={{ margin: 0 }}>Salve o rascunho para recalcular os ajustes e os avisos.</p>}
              </div>
              {pode && <RodapeFormulario sujo={sujo} valido={bloqueiosLocais.length === 0} salvando={salvando} aoSalvar={() => void salvarRascunho()}
                aoCancelar={() => { setTextos(textosDe(resumo.rascunho)); setSujo(false); }} rotuloSalvar="Salvar rascunho" />}
            </>
          )}
          {aba === "historico" && (
            <div className="tl-painel-aba">
              {resumo.historico.length === 0 ? <p className="nx-suave">Ainda não houve publicação: vale a marca padrão da instalação.</p> : (
                <ol className="tl-linha-tempo">
                  {resumo.historico.map((h) => (
                    <li key={h.versao}><b>Versão {h.versao} · {ORIGEM[h.origem] ?? h.origem}{h.restaurada_de ? ` (da versão ${h.restaurada_de})` : ""} · {h.produto}</b>
                      <small>{new Date(h.publicado_em).toLocaleString("pt-BR")} ({idadeRelativa(h.publicado_em)}) · {h.publicado_por} · “{h.motivo}”</small>
                      {pode && h.versao !== resumo.publicada.versao && (
                        <div><button type="button" className="tl-fantasma" onClick={() => setDialogo({ tipo: "restaurar", versao: h.versao })}>Restaurar esta versão…</button></div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              <p className="nx-suave" style={{ margin: 0, fontSize: 12 }}>O histórico não se apaga: restaurar publica uma versão nova igual à escolhida.</p>
              {pode && <div><button type="button" className="nx-botao" onClick={() => setDialogo({ tipo: "padrao" })}>Voltar ao padrão…</button></div>}
            </div>
          )}
          {aba === "pacote" && (
            <div className="tl-painel-aba">
              <p style={{ margin: 0 }}>O pacote (.zip) leva textos, cores e imagens, conferidos por SHA-256. Use para subir um integrador novo ou repetir a marca numa
                instalação de homologação. Importar põe no rascunho, para você revisar e publicar.</p>
              <div className="tl-linha-acoes">
                <button type="button" className="nx-botao" onClick={() => void exportar()}>Exportar a marca publicada</button>
                {pode && <label className="nx-botao">Importar pacote…<input type="file" accept=".zip,application/zip" className="visualmente-oculto"
                  onChange={(e) => void importar(e)} /></label>}
              </div>
              <p className="nx-suave" style={{ margin: 0, fontSize: 12 }}>Sem tela: <code>python -m backend.gerenciar_marca importar --pacote marca.zip --autor implantacao</code></p>
            </div>
          )}
        </section>
        <aside className="previas-marca" aria-label="Prévia nos três modos">
          {(["escuro", "claro", "contraste"] as const).map((m) => <Previa key={m} modo={m} textos={textos} rascunho={resumo.rascunho} cores={v.cores_por_modo} />)}
        </aside>
      </div>
      <DialogoMotivo aberto={dialogo !== null} rotuloConfirmar={dialogo?.tipo === "publicar" ? "Publicar" : dialogo?.tipo === "padrao" ? "Voltar ao padrão" : "Restaurar"}
        titulo={dialogo?.tipo === "publicar" ? "Publicar a marca" : dialogo?.tipo === "padrao" ? "Voltar à marca padrão" : `Restaurar a versão ${dialogo?.versao ?? ""}`}
        texto="Todas as telas, e-mails, canais e relatórios passam a usar esta marca. Fica uma versão nova no histórico."
        exemplo="Ex.: marca do integrador Aqua, contrato de 28/10" aoCancelar={() => setDialogo(null)} aoConfirmar={confirmar} />
    </section>
  );
}
